import { Unit, UnitType, Position, FactionType } from './types';
import { UNIT_DEFINITIONS } from './unitDefinitions';

export class UnitManager {
  private units: Map<string, Unit> = new Map();
  private nextId: number = 1;
  private unitCounters: Map<string, number> = new Map(); // Track unit numbers by type and faction
  private lastMovedUnit: Map<string, string> = new Map(); // Track last moved unit ID per faction

  constructor() {}

  createUnit(
    type: UnitType, 
    faction: FactionType, 
    position: Position
  ): Unit {
    const definition = UNIT_DEFINITIONS[type];
    // Generate globally unique ID using timestamp + random to avoid conflicts in multiplayer
    const id = `unit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Generate unit name with sequential numbering
    const counterKey = `${faction}_${type}`;
    const unitNumber = (this.unitCounters.get(counterKey) || 0) + 1;
    this.unitCounters.set(counterKey, unitNumber);
    
    const unitName = this.generateUnitName(type, unitNumber);
    
    const unit: Unit = {
      id,
      name: unitName,
      type,
      faction,
      position: { ...position },
      stats: {
        movement: definition.movement,
        attack: definition.attack,
        defense: definition.defense,
        health: definition.maxHealth,
        maxHealth: definition.maxHealth,
        range: definition.range,
        cost: definition.cost
      },
      movementRemaining: definition.movement,
      hasAttacked: false,
      displayPosition: { ...position }, // Start display position at actual position
      category: definition.category,
      level: definition.level,
      // Veteran system
      killCount: 0,
      isVeteran: false
    };

    this.units.set(id, unit);
    return unit;
  }

  addExistingUnit(unit: Unit): void {
    // Add an existing unit to the manager without creating a new one
    // This is used when syncing units from Firebase
    // Ensure displayPosition is set for renderer
    if (!unit.displayPosition) {
      unit.displayPosition = { x: unit.position.x, y: unit.position.y };
    }
    this.units.set(unit.id, unit);
  }

  private generateUnitName(type: UnitType, number: number): string {
    const ordinal = this.getOrdinal(number);
    
    switch(type) {
      // US Units
      case UnitType.RIFLE_SQUAD:
        return `${ordinal} Rifle Squad`;
      case UnitType.RANGER_SQUAD:
        return `${ordinal} Ranger Squad`;
      case UnitType.AIRBORNE_SQUAD:
        return `${ordinal} Airborne Squad`;
      case UnitType.SHERMAN_SECTION:
        return `${ordinal} Sherman Tank`;
      case UnitType.TANK_DESTROYER_SECTION:
        return `${ordinal} M10 Wolverine`;
      case UnitType.US_ARTILLERY_SECTION:
        return `${ordinal} M101 Howitzer Battery`;
      case UnitType.ENGINEER_SQUAD:
        return `${ordinal} Engineer Squad`;
      
      // German Units
      case UnitType.PANZERGRENADIER_SQUAD:
        return `${ordinal} Panzergrenadier Squad`;
      case UnitType.WAFFEN_SS_SQUAD:
        return `${ordinal} Waffen-SS Squad`;
      case UnitType.FALLSCHIRMJAGER_SQUAD:
        return `${ordinal} Fallschirmjäger Squad`;
      case UnitType.PANZER_IV_ZUG:
        return `${ordinal} Panzer IV`;
      case UnitType.TIGER_I_SECTION:
        return `${ordinal} Tiger I`;
      case UnitType.STUG_III_SECTION:
        return `${ordinal} StuG III`;
      case UnitType.GER_ARTILLERY_SECTION:
        return `${ordinal} Pak 40 Battery`;
      case UnitType.PIONEER_SQUAD:
        return `${ordinal} Pioneer Squad`;
      
      // Eastern Wehrmacht Units (Stalingrad 1942)
      case UnitType.GRENADIER_SQUAD_1942:
        return `${ordinal} Grenadier Squad`;
      case UnitType.STURM_ABTEILUNG_SQUAD:
        return `${ordinal} Sturm-Abteilung Squad`;
      case UnitType.FALLSCHIRMJAGER_SQUAD_1942:
        return `${ordinal} Fallschirmjäger Squad`;
      case UnitType.PANZERGRENADIER_SQUAD_1942:
        return `${ordinal} Panzergrenadier Squad`;
      case UnitType.STURMPIONIERE:
        return `${ordinal} Sturmpioniere`;
      case UnitType.SANITATER_SQUAD_1942:
        return `${ordinal} Sanitäter Squad`;
      case UnitType.PANZER_IV_AUSF_F2:
        return `${ordinal} Panzer IV`;
      case UnitType.PANZER_III_AUSF_L:
        return `${ordinal} Panzer III`;
      case UnitType.STUG_III_AUSF_F:
        return `${ordinal} StuG III`;
      case UnitType.PAK_38_BATTERY:
        return `${ordinal} Pak 38 Battery`;
      case UnitType.SDKFZ_250_1:
        return `${ordinal} Sd.Kfz. 250`;
      case UnitType.JU_87_D_STUKA:
        return `${ordinal} Ju 87 Stuka`;
      case UnitType.HE_111_H6:
        return `${ordinal} He 111`;
      case UnitType.OPEL_BLITZ_1942:
        return `${ordinal} Opel Blitz`;
      case UnitType.SDKFZ_251_1942:
        return `${ordinal} Sd.Kfz. 251`;
      
      // UK Units
      case UnitType.INFANTRY_SECTION:
        return `${ordinal} Infantry Section`;
      case UnitType.GUARDS_INFANTRY:
        return `${ordinal} Guards Infantry`;
      case UnitType.COMMANDO_SECTION:
        return `${ordinal} Commando Section`;
      case UnitType.SAPPER_SECTION:
        return `${ordinal} Sapper Section`;
      case UnitType.CRUSADER_TANK:
        return `${ordinal} Crusader Tank`;
      case UnitType.MATILDA_II:
        return `${ordinal} Matilda II`;
      case UnitType.SIX_POUNDER_AT:
        return `${ordinal} 6-Pounder AT`;
      case UnitType.TWENTY_FIVE_POUNDER:
        return `${ordinal} 25-Pounder Battery`;
      
      // Afrika Korps Units
      case UnitType.SCHUTZEN_SQUAD:
        return `${ordinal} Schützen Squad`;
      case UnitType.AFRIKA_GRENADIERS:
        return `${ordinal} Afrika Grenadiers`;
      case UnitType.AK_PIONEER_SQUAD:
        return `${ordinal} Afrika Pioneer Squad`;
      case UnitType.AK_FALLSCHIRMJAGER:
        return `${ordinal} Fallschirmjäger`;
      case UnitType.PANZER_III:
        return `${ordinal} Panzer III`;
      case UnitType.PANZER_II:
        return `${ordinal} Panzer II`;
      case UnitType.EIGHTY_EIGHT_FLAK:
        return `${ordinal} 88mm Flak`;
      case UnitType.PAK_38:
        return `${ordinal} Pak 38`;
      
      // Recon Units
      case UnitType.M8_GREYHOUND:
        return `${ordinal} M8 Greyhound`;
      case UnitType.SDKFZ_222:
        return `${ordinal} Sd.Kfz. 222`;
      case UnitType.DAIMLER_DINGO:
        return `${ordinal} Daimler Dingo`;
      case UnitType.SDKFZ_250_9:
        return `${ordinal} Sd.Kfz. 250/9`;
      
      // Soviet Units
      case UnitType.RED_ARMY_SQUAD:
        return `${ordinal} Red Army Squad`;
      case UnitType.CONSCRIPT_SQUAD:
        return `${ordinal} Conscript Squad`;
      case UnitType.SHOCK_TROOPS:
        return `${ordinal} Shock Troops`;
      case UnitType.T34_76:
        return `${ordinal} T-34 Tank`;
      case UnitType.KV1_HEAVY_TANK:
        return `${ordinal} KV-1 Tank`;
      case UnitType.T70_LIGHT_TANK:
        return `${ordinal} T-70 Light Tank`;
      case UnitType.BA64_ARMORED_CAR:
        return `${ordinal} BA-64 Armored Car`;
      case UnitType.ZIS3_AT_GUN:
        return `${ordinal} ZiS-3 AT Gun`;
      case UnitType.SOVIET_SAPPER_SQUAD:
        return `${ordinal} Sapper Squad`;
      case UnitType.KATYUSHA_BATTERY:
        return `${ordinal} Katyusha Battery`;
      
      // US Marine Units (Okinawa)
      case UnitType.MARINE_SQUAD:
        return `${ordinal} Marine Squad`;
      case UnitType.MARINE_RAIDERS:
        return `${ordinal} Marine Raiders`;
      case UnitType.MARINE_FLAME_TEAM:
        return `${ordinal} Marine Flame Team`;
      case UnitType.MARINE_ENGINEERS:
        return `${ordinal} Marine Engineers`;
      case UnitType.M4A3_SHERMAN:
        return `${ordinal} M4A3 Sherman`;
      case UnitType.M3_STUART:
        return `${ordinal} M3 Stuart`;
      case UnitType.M7_PRIEST:
        return `${ordinal} M7 Priest`;
      case UnitType.PACK_HOWITZER:
        return `${ordinal} Pack Howitzer`;
      
      // Imperial Japan Units (Okinawa)
      case UnitType.IJA_SQUAD:
        return `${ordinal} IJA Squad`;
      case UnitType.SNLF_SQUAD:
        return `${ordinal} SNLF Squad`;
      case UnitType.ASSAULT_PIONEERS:
        return `${ordinal} Assault Pioneers`;
      case UnitType.CAVE_DEFENSE_SQUAD:
        return `${ordinal} Cave Defense Squad`;
      case UnitType.TYPE_95_HA_GO:
        return `${ordinal} Type 95 Ha-Go`;
      case UnitType.TYPE_97_CHI_HA:
        return `${ordinal} Type 97 Chi-Ha`;
      case UnitType.TYPE_4_CHI_TO:
        return `${ordinal} Type 4 Chi-To`;
      case UnitType.TYPE_92_JYU_SOKOSHA:
        return `${ordinal} Type 92 Jyu-Sokosha`;
      case UnitType.TYPE_92_BATTALION_GUN:
        return `${ordinal} Type 92 Battalion Gun`;
      case UnitType.TYPE_89_MORTAR:
        return `${ordinal} Type 89 Mortar`;
      case UnitType.TYPE_1_47MM_AT_GUN:
        return `${ordinal} Type 1 47mm AT Gun`;
      case UnitType.TYPE_96_150MM_HOWITZER:
        return `${ordinal} Type 96 150mm Howitzer`;
      
      // Aircraft Units
      // US Aircraft
      case UnitType.P51_MUSTANG:
        return `${ordinal} P-51 Mustang`;
      case UnitType.B24_LIBERATOR:
        return `${ordinal} B-24 Liberator`;
      
      // German Aircraft
      case UnitType.BF109_FIGHTER:
        return `${ordinal} Bf 109`;
      case UnitType.JU88_BOMBER:
        return `${ordinal} Ju 88`;
      
      // British Aircraft
      case UnitType.SPITFIRE_MK_V:
        return `${ordinal} Spitfire`;
      case UnitType.LANCASTER_BOMBER:
        return `${ordinal} Lancaster`;
      
      // Soviet Aircraft
      case UnitType.YAK_3_FIGHTER:
        return `${ordinal} Yak-3`;
      case UnitType.IL2_STURMOVIK:
        return `${ordinal} Il-2 Sturmovik`;
      
      // Japanese Aircraft
      case UnitType.A6M_ZERO:
        return `${ordinal} A6M Zero`;
      case UnitType.G4M_BETTY:
        return `${ordinal} G4M Betty`;
      
      // Canadian Units
      case UnitType.CANADIAN_INFANTRY_SECTION:
        return `${ordinal} Infantry Section`;
      case UnitType.PPCLI:
        return `${ordinal} PPCLI`;
      case UnitType.HIGHLAND_REGIMENT:
        return `${ordinal} Highland Regiment`;
      case UnitType.COMBAT_ENGINEERS:
        return `${ordinal} Combat Engineers`;
      case UnitType.RAM_TANK:
        return `${ordinal} Ram Tank`;
      case UnitType.SEXTON_SPG:
        return `${ordinal} Sexton SPG`;
      case UnitType.TWENTY_FIVE_POUNDER_BATTERY:
        return `${ordinal} 25-Pounder Battery`;
      case UnitType.RECONNAISSANCE_PATROL:
        return `${ordinal} Recce Patrol`;
      case UnitType.SHERMAN_FIREFLY:
        return `${ordinal} Sherman Firefly`;
      case UnitType.SPITFIRE_FIGHTER:
        return `${ordinal} Spitfire`;
      case UnitType.MOSQUITO_FIGHTER_BOMBER:
        return `${ordinal} Mosquito`;
      
      // Italian Units
      case UnitType.BERSAGLIERI_SQUAD:
        return `${ordinal} Bersaglieri Squad`;
      case UnitType.ALPINI_MOUNTAIN_TROOPS:
        return `${ordinal} Alpini Mountain Troops`;
      case UnitType.FOLGORE_PARATROOPERS:
        return `${ordinal} Folgore Paratroopers`;
      case UnitType.GENIO_ENGINEERS:
        return `${ordinal} Genio Engineers`;
      case UnitType.M15_42_TANK:
        return `${ordinal} M15/42 Tank`;
      case UnitType.SEMOVENTE_75_18:
        return `${ordinal} Semovente 75/18`;
      case UnitType.CANNONE_75_32:
        return `${ordinal} Cannone 75/32`;
      case UnitType.ARDITI_SCOUTS:
        return `${ordinal} Arditi Scouts`;
      case UnitType.P26_40_HEAVY_TANK:
        return `${ordinal} P26/40 Heavy Tank`;
      case UnitType.MACCHI_C205:
        return `${ordinal} Macchi C.205`;
      case UnitType.SM79_BOMBER:
        return `${ordinal} SM.79 Bomber`;
      
      // New Elite Units
      case UnitType.HEAVY_WEAPONS_SQUAD:
        return `${ordinal} Heavy Weapons Squad`;
      case UnitType.GEBIRGSJAGER_SQUAD:
        return `${ordinal} Gebirgsjäger Squad`;
      case UnitType.BRANDENBURG_COMMANDOS:
        return `${ordinal} Brandenburg Commandos`;
      case UnitType.SAN_MARCO_MARINES:
        return `${ordinal} San Marco Marines`;
      
      // New Airborne Units
      case UnitType.RED_DEVILS_PARACHUTE:
        return `${ordinal} Red Devils`;
      case UnitType.VDV_GUARDS_AIRBORNE:
        return `${ordinal} VDV Guards`;
      case UnitType.CANADIAN_PARACHUTE_BATTALION:
        return `${ordinal} Parachute Battalion`;
      case UnitType.MARINE_PARACHUTE_REGIMENT:
        return `${ordinal} Marine Paratroopers`;
      case UnitType.TEISHIN_SHUDAN:
        return `${ordinal} Teishin Shudan`;
      
      // Medic Units
      case UnitType.MEDICAL_CORPS_SQUAD:
        return `${ordinal} Medical Corps`;
      case UnitType.NAVY_CORPSMEN:
        return `${ordinal} Navy Corpsmen`;
      case UnitType.FIELD_AMBULANCE_SECTION:
        return `${ordinal} Field Ambulance`;
      case UnitType.MEDICAL_BATTALION:
        return `${ordinal} Medical Battalion`;
      case UnitType.SANITATER_SQUAD:
        return `${ordinal} Sanitäter Squad`;
      case UnitType.AK_SANITATER:
        return `${ordinal} Afrika Sanitäter`;
      case UnitType.MEDICAL_SERVICE_CORPS:
        return `${ordinal} Medical Service`;
      case UnitType.SANITA_MILITARE:
        return `${ordinal} Sanità Militare`;
      case UnitType.IJA_MEDICAL_CORPS:
        return `${ordinal} Medical Corps`;
      
      // Transport Aircraft
      case UnitType.C47_SKYTRAIN:
        return `${ordinal} C-47 Skytrain`;
      case UnitType.R4D_SKYTRAIN:
        return `${ordinal} R4D Skytrain`;
      case UnitType.STIRLING_TRANSPORT:
        return `${ordinal} Stirling Transport`;
      case UnitType.LI2_TRANSPORT:
        return `${ordinal} Li-2 Transport`;
      case UnitType.JU52_TRANSPORT:
        return `${ordinal} Ju 52 Transport`;
      case UnitType.JU52_AFRIKA:
        return `${ordinal} Ju 52 Afrika`;
      case UnitType.DAKOTA_TRANSPORT:
        return `${ordinal} Dakota Transport`;
      case UnitType.SM82_MARSUPIALE:
        return `${ordinal} SM.82 Marsupiale`;
      case UnitType.KI57_TRANSPORT:
        return `${ordinal} Ki-57 Transport`;
      case UnitType.GRIZZLY_CRUISER:
        return `${ordinal} Grizzly Cruiser`;
      case UnitType.OBICE_149_19:
        return `${ordinal} 149mm Howitzer`;
      
      // Ground Transport Vehicles - US
      case UnitType.GMC_CCKW:
        return `${ordinal} GMC Truck`;
      case UnitType.M3_HALFTRACK:
        return `${ordinal} M3 Halftrack`;
      case UnitType.DUKW_AMPHIBIOUS:
        return `${ordinal} DUKW`;
      case UnitType.LVT_4_AMTRAC:
        return `${ordinal} LVT(A)-4`;
        
      // Ground Transport Vehicles - German
      case UnitType.OPEL_BLITZ:
        return `${ordinal} Opel Blitz`;
      case UnitType.SDKFZ_251:
        return `${ordinal} Sd.Kfz. 251`;
      case UnitType.STEYR_1500A:
        return `${ordinal} Steyr 1500A`;
      case UnitType.SDKFZ_251_AFRIKA:
        return `${ordinal} Sd.Kfz. 251 Afrika`;
        
      // Ground Transport Vehicles - Soviet
      case UnitType.ZIS_5:
        return `${ordinal} ZiS-5`;
      case UnitType.M3A1_SCOUT_CAR:
        return `${ordinal} M3A1 Scout Car`;
        
      // Ground Transport Vehicles - British
      case UnitType.BEDFORD_OYD:
        return `${ordinal} Bedford`;
      case UnitType.LOYD_CARRIER:
        return `${ordinal} Loyd Carrier`;
        
      // Ground Transport Vehicles - Japanese
      case UnitType.ISUZU_TYPE_94:
        return `${ordinal} Type 94 Truck`;
      case UnitType.TYPE_1_HO_HA:
        return `${ordinal} Type 1 Ho-Ha`;
        
      // Ground Transport Vehicles - Canadian
      case UnitType.CMP_TRUCK:
        return `${ordinal} CMP Truck`;
      case UnitType.PRIEST_KANGAROO:
        return `${ordinal} Priest Kangaroo`;
        
      // Ground Transport Vehicles - Italian
      case UnitType.FIAT_626:
        return `${ordinal} Fiat 626`;
      case UnitType.FIAT_SPA_DOVUNQUE:
        return `${ordinal} Fiat SPA`;
      
      // France 1940 Units
      case UnitType.FRENCH_INFANTRY_SQUAD:
        return `${ordinal} French Infantry Squad`;
      case UnitType.CHASSEURS_ALPINS:
        return `${ordinal} Chasseurs Alpins`;
      case UnitType.FOREIGN_LEGION_SQUAD:
        return `${ordinal} Foreign Legion`;
      case UnitType.COLONIAL_TROOPS:
        return `${ordinal} Colonial Troops`;
      case UnitType.FRENCH_ENGINEERS:
        return `${ordinal} Sapeurs`;
      case UnitType.FRENCH_MEDICAL_CORPS:
        return `${ordinal} Service de Santé`;
      case UnitType.CHAR_B1_BIS:
        return `${ordinal} Char B1 bis`;
      case UnitType.SOMUA_S35:
        return `${ordinal} SOMUA S35`;
      case UnitType.HOTCHKISS_H39:
        return `${ordinal} Hotchkiss H39`;
      case UnitType.CANON_75MM:
        return `${ordinal} Canon de 75`;
      case UnitType.CANON_47MM_AT:
        return `${ordinal} Canon de 47 antichar`;
      case UnitType.FRENCH_RECON:
        return `${ordinal} AMD Panhard 178`;
      case UnitType.MS_406_FIGHTER:
        return `${ordinal} MS.406`;
      case UnitType.POTEZ_63_BOMBER:
        return `${ordinal} Potez 63`;
      case UnitType.RENAULT_AHS:
        return `${ordinal} Renault AHS`;
      case UnitType.LORRAINE_38L:
        return `${ordinal} Lorraine 38L`;
      
      // Wehrmacht 1940 Units
      case UnitType.WEHRMACHT_INFANTRY_1940:
        return `${ordinal} Wehrmacht Infantry`;
      case UnitType.STOSSTRUPPEN_1940:
        return `${ordinal} Stoßtruppen`;
      case UnitType.GEBIRGSJAGER_1940:
        return `${ordinal} Gebirgsjäger`;
      case UnitType.MOTORIZED_INFANTRY_1940:
        return `${ordinal} Panzergrenadiers`;
      case UnitType.PIONIERE_1940:
        return `${ordinal} Pioniere`;
      case UnitType.SANITATER_1940:
        return `${ordinal} Sanitäter`;
      case UnitType.PANZER_III_E:
        return `${ordinal} Panzer III`;
      case UnitType.PANZER_II_C:
        return `${ordinal} Panzer II`;
      case UnitType.PANZER_38T:
        return `${ordinal} Panzer 38(t)`;
      case UnitType.STUG_III_A:
        return `${ordinal} StuG III`;
      case UnitType.PAK_36_BATTERY:
        return `${ordinal} Pak 36`;
      case UnitType.AUFKLARUNG_1940:
        return `${ordinal} Sd.Kfz. 222`;
      case UnitType.BF_109E_FIGHTER:
        return `${ordinal} Bf 109E`;
      case UnitType.JU_87B_STUKA:
        return `${ordinal} Ju 87 Stuka`;
      case UnitType.OPEL_BLITZ_1940:
        return `${ordinal} Opel Blitz`;
      case UnitType.SDKFZ_251_AUSF_A:
        return `${ordinal} Sd.Kfz. 251`;
      
      // Italian 1940 Units
      case UnitType.REGULAR_INFANTRY_1940:
        return `${ordinal} Regular Infantry`;
      case UnitType.ALPINI_1940:
        return `${ordinal} Alpini`;
      case UnitType.BLACKSHIRT_BATTALION:
        return `${ordinal} Blackshirt Battalion`;
      case UnitType.LIBYAN_COLONIAL_TROOPS:
        return `${ordinal} Libyan Colonial Troops`;
      case UnitType.GENIO_SAPPERS_1940:
        return `${ordinal} Genio Sappers`;
      case UnitType.REGIO_MEDICI:
        return `${ordinal} Regio Medici`;
      case UnitType.L3_33_TANKETTE:
        return `${ordinal} L3/33 Tankette`;
      case UnitType.M11_39_TANK:
        return `${ordinal} M11/39 Tank`;
      case UnitType.L6_40_LIGHT_TANK:
        return `${ordinal} L6/40 Light Tank`;
      case UnitType.CANNONE_47_32:
        return `${ordinal} 47/32 AT Gun`;
      case UnitType.OBICE_100_17:
        return `${ordinal} 100/17 Howitzer`;
      case UnitType.AB_40_ARMORED_CAR:
        return `${ordinal} AB 40 Armored Car`;
      case UnitType.CR_42_FALCO:
        return `${ordinal} CR.42 Falco`;
      case UnitType.SM_79_SPARVIERO:
        return `${ordinal} SM.79 Sparviero`;
      case UnitType.FIAT_626:
        return `${ordinal} Fiat 626 Truck`;
      case UnitType.FIAT_SPA_AUTOPROTETTO:
        return `${ordinal} SPA Autoprotetto`;
        
      // Greek 1940 Units
      case UnitType.EVZONES_REGIMENT:
        return `${ordinal} Evzones Regiment`;
      case UnitType.REGULAR_INFANTRY_DIVISION:
        return `${ordinal} Infantry Division`;
      case UnitType.CRETAN_DIVISION:
        return `${ordinal} Cretan Division`;
      case UnitType.NATIONAL_GUARD_BATTALION:
        return `${ordinal} National Guard`;
      case UnitType.ENGINEER_COMPANY:
        return `${ordinal} Engineer Company`;
      case UnitType.MEDICAL_SECTION:
        return `${ordinal} Medical Section`;
      case UnitType.VICKERS_6TON_TANK:
        return `${ordinal} Vickers Tank`;
      case UnitType.FT_17_RENAULT:
        return `${ordinal} FT-17 Renault`;
      case UnitType.CARDEN_LOYD_TANKETTE:
        return `${ordinal} Carden-Loyd`;
      case UnitType.CAPTURED_L3_TANKETTE:
        return `${ordinal} Captured L3`;
      case UnitType.SCHNEIDER_75MM_MOUNTAIN_GUN:
        return `${ordinal} Mountain Gun`;
      case UnitType.SKODA_75MM_FIELD_GUN:
        return `${ordinal} Field Gun`;
      case UnitType.PZL_P24_FIGHTER:
        return `${ordinal} PZL P.24`;
      case UnitType.FAIREY_BATTLE_BOMBER:
        return `${ordinal} Fairey Battle`;
      case UnitType.FORD_V8_TRUCK:
        return `${ordinal} Ford V8`;
      case UnitType.DODGE_T110L_HALFTRACK:
        return `${ordinal} Dodge Halftrack`;
        
      default:
        return `${ordinal} Unit`;
    }
  }

  private getOrdinal(n: number): string {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  }

  getUnit(id: string): Unit | undefined {
    return this.units.get(id);
  }

  getUnitAt(position: Position): Unit | undefined {
    for (const unit of this.units.values()) {
      if (unit.position.x === position.x && unit.position.y === position.y) {
        return unit;
      }
    }
    return undefined;
  }

  getAllUnits(): Unit[] {
    return Array.from(this.units.values());
  }

  getUnitsForFaction(faction: FactionType): Unit[] {
    return Array.from(this.units.values()).filter(unit => unit.faction === faction);
  }

  moveUnit(unitId: string, newPosition: Position, movementCost: number): boolean {
    const unit = this.units.get(unitId);
    if (!unit) return false;

    // Check if there's already a unit at the destination
    if (this.getUnitAt(newPosition)) return false;

    // Check if unit has enough movement
    if (unit.movementRemaining < movementCost) return false;

    // Set up animation
    if (!unit.displayPosition) {
      unit.displayPosition = { ...unit.position };
    }
    unit.targetPosition = { ...newPosition };
    unit.animationStartTime = Date.now();
    
    // Move the unit
    unit.position = { ...newPosition };
    unit.movementRemaining -= movementCost;
    
    // Track last moved unit for this faction
    this.lastMovedUnit.set(unit.faction, unit.id);
    
    return true;
  }

  resetUnitMovement(faction: FactionType): void {
    for (const unit of this.units.values()) {
      if (unit.faction === faction) {
        // Use unit's actual stats.movement (which includes veteran bonuses) 
        // NOT the base definition movement
        
        // Hunkered units don't regain movement
        if (!(unit as any).isHunkered) {
          unit.movementRemaining = unit.stats.movement;
        } else {
          unit.movementRemaining = 0;
        }
        
        unit.hasAttacked = false;
        (unit as any).hasPlacedMine = false; // Reset mine placement for engineers
        (unit as any).hasHealed = false; // Reset healing/repair for medics and engineers
        (unit as any).hunkerChangedThisTurn = false; // Reset hunker change flag
        unit.hasBoarded = false; // Reset boarding/disembarking for this turn
        unit.hasTowed = false; // Reset towing/untowing for this turn
      }
    }
  }

  removeUnit(id: string): void {
    this.units.delete(id);
  }

  damageUnit(id: string, damage: number, attackerFaction?: string, attackerId?: string): { destroyed: boolean, reward?: number, attackerPromoted?: boolean } {
    const unit = this.units.get(id);
    if (!unit) return { destroyed: false };

    unit.stats.health -= damage;
    
    // Mark unit as destroyed but don't remove yet (let death animation play)
    if (unit.stats.health <= 0) {
      const reward = attackerFaction ? Math.floor(unit.stats.cost * 0.5) : 0;
      unit.stats.health = 0; // Ensure health is exactly 0
      
      // Handle veteran system - increment attacker's kill count
      let attackerPromoted = false;
      if (attackerId) {
        const attacker = this.units.get(attackerId);
        if (attacker && !attacker.isVeteran) {
          const previousKillCount = attacker.killCount || 0;
          attacker.killCount = previousKillCount + 1;
          
          console.log(`[VETERAN] ${attacker.name} kill count: ${previousKillCount} -> ${attacker.killCount}`);
          
          // Check for veteran promotion (requires exactly 3 kills)
          if (attacker.killCount >= 3) {
            console.log(`[VETERAN] ${attacker.name} promoted to veteran with ${attacker.killCount} kills`);
            attackerPromoted = this.promoteToVeteran(attacker);
          }
        } else if (attacker && attacker.isVeteran) {
          // Still track kills for veterans
          attacker.killCount = (attacker.killCount || 0) + 1;
        }
      }
      
      // Don't remove the unit yet - let the death animation play first
      return { destroyed: true, reward, attackerPromoted }; // Unit was destroyed, return reward and promotion status
    }
    
    return { destroyed: false }; // Unit survived
  }
  
  private promoteToVeteran(unit: Unit): boolean {
    if (unit.isVeteran) return false;
    
    unit.isVeteran = true;
    
    // Store original stats if not already stored
    if (!unit.originalStats) {
      unit.originalStats = { ...unit.stats };
    }
    
    // Apply 25% stat boost
    const baseStats = unit.originalStats;
    unit.stats.attack = Math.round(baseStats.attack * 1.25);
    unit.stats.defense = Math.round(baseStats.defense * 1.25);
    unit.stats.movement = Math.ceil(baseStats.movement * 1.25); // Round up for movement
    unit.stats.maxHealth = Math.round(baseStats.maxHealth * 1.25);
    
    // Fully restore health
    unit.stats.health = unit.stats.maxHealth;
    
    // Also restore movement for current turn
    unit.movementRemaining = unit.stats.movement;
    
    // Update name to include veteran status
    if (!unit.name.includes('Veteran')) {
      unit.name = `Veteran ${unit.name}`;
    }
    
    return true;
  }

  getLastMovedUnit(faction: FactionType): Unit | undefined {
    const unitId = this.lastMovedUnit.get(faction);
    if (!unitId) return undefined;
    return this.units.get(unitId);
  }

  // Method to restore saved units
  restoreUnit(unitData: any): void {
    // Ensure all properties are preserved, including dynamic ones
    const unit: Unit = {
      ...unitData,
      // Explicitly restore properties that might be lost
      hometown: unitData.hometown,
      carryingUnit: unitData.carryingUnit,
      carryingUnits: unitData.carryingUnits,
      carryingUnitData: unitData.carryingUnitData,
      carryingUnitsData: unitData.carryingUnitsData
    };
    
    // Also restore any other dynamic properties
    if (unitData.isHunkered !== undefined) {
      (unit as any).isHunkered = unitData.isHunkered;
    }
    if (unitData.hunkerChangedThisTurn !== undefined) {
      (unit as any).hunkerChangedThisTurn = unitData.hunkerChangedThisTurn;
    }
    
    this.units.set(unit.id, unit);
    
    // Update nextId to avoid conflicts
    const match = unit.id.match(/unit_(\d+)/);
    if (match) {
      const unitNum = parseInt(match[1]);
      if (unitNum >= this.nextId) {
        this.nextId = unitNum + 1;
      }
    }
    
    // Update unit counters
    const counterKey = `${unit.faction}_${unit.type}`;
    const currentCount = this.unitCounters.get(counterKey) || 0;
    // Extract unit number from name if possible
    const nameMatch = unit.name.match(/^(\d+)\w+/);
    if (nameMatch) {
      const unitNumber = parseInt(nameMatch[1]);
      if (unitNumber > currentCount) {
        this.unitCounters.set(counterKey, unitNumber);
      }
    }
  }
  
  // Clear all units
  clearAllUnits(): void {
    this.units.clear();
    this.lastMovedUnit.clear();
  }
}